import React, { useEffect, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { fetchSiteMode } from "./Features/SiteMode/siteModeSlice";

import "./App.css";

import Header from "../src/Components/Header/Navbar";
import Footer from "../src/Components/Footer/Footer";
import ScrollToTop from "./Components/ScrollButton/ScrollToTop";
import { Toaster } from "react-hot-toast";
import { fetchWishlist } from "./Features/Wishlist/wishListSlice";
import { trackPageView } from "./utils/facebookPixel";
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import Roulette from "./Components/Roulette/Roulette";

// Lazy load pages for performance optimization (code splitting)
const Home = lazy(() => import("../src/Pages/Home"));
const About = lazy(() => import("../src/Pages/About"));
const Shop = lazy(() => import("../src/Pages/Shop"));
const Contact = lazy(() => import("../src/Pages/Contact"));
const ProductDetails = lazy(() => import("./Pages/ProductDetails"));
const NotFound = lazy(() => import("./Pages/NotFound"));
const Authentication = lazy(() => import("./Pages/Authentication"));
const ResetPass = lazy(() => import("./Components/Authentication/Reset/ResetPass"));
const TermsConditions = lazy(() => import("./Pages/TermsConditions"));
const ShoppingCart = lazy(() => import("./Components/ShoppingCart/ShoppingCart"));
const LinksPage = lazy(() => import("./Pages/LinksPage"));
const Profile = lazy(() => import("./Pages/Profile"));
const Wishlist = lazy(() => import("./Pages/Wishlist"));

// Import test utilities for development (remove in production)
if (process.env.NODE_ENV === 'development') {
  import('./utils/facebookPixelTest');
  import('./utils/pixelDebug');
}

// Component to track page views
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    // Track page view when route changes
    const pageName = location.pathname === '/' ? 'Home' : location.pathname.substring(1);
    trackPageView(pageName);
  }, [location]);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { mode, loading: modeLoading } = useSelector((state) => state.siteMode);

  // Fetch site display mode on startup and poll every 10 seconds
  useEffect(() => {
    // Initial fetch
    dispatch(fetchSiteMode());

    // Set up polling to "listen" for changes from the app
    const interval = setInterval(() => {
      dispatch(fetchSiteMode());
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  // Track the previous mode to detect changes during the session
  const prevModeRef = React.useRef(null);

  useEffect(() => {
    // Only set the initial ref once loading is finished for the first time
    if (!modeLoading && prevModeRef.current === null) {
      prevModeRef.current = mode;
    }
    
    // If the mode changes after we have an initial value, then we reload
    if (!modeLoading && prevModeRef.current !== null && prevModeRef.current !== mode) {
      // Update ref before reload to be safe (though reload will reset it anyway)
      prevModeRef.current = mode;
      window.location.reload();
    }
  }, [mode, modeLoading]);

  useEffect(() => {
    if (token) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, token]);

  // Show a minimal loading screen while resolving the site mode
  if (modeLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafaf8',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '2px',
            color: '#1a1a1a',
            marginBottom: '1rem',
          }}>Lellehom</div>
          <div style={{ color: '#888', fontSize: '1rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // ── LINKS-ONLY MODE ──────────────────────────────────────────────────────────
  // When activated from the mobile app, ALL visitors see only the LinksPage.
  // The URL in the browser stays exactly as it was (no redirect).
if (mode === 'links_only') {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div>Loading...</div>}>
        <LinksPage />
      </Suspense>
    </ErrorBoundary>
  );
}

  // ── FULL WEBSITE MODE ────────────────────────────────────────────────────────
return (
  <ErrorBoundary>
    <ScrollToTop />
    <BrowserRouter>
      <Roulette />
      <PageTracker />
      <Header />
      <div className="main-content-spacer"></div>
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/product/:slug-:id" element={<ProductDetails />} />
          <Route path="/product/:id" element={<ProductDetails />} />
          <Route path="/login" element={<Authentication />} />
          <Route path="/resetPassword" element={<ResetPass />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/cart" element={<ShoppingCart />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/links" element={<LinksPage />} />
        </Routes>
      </Suspense>
      <Footer />
      <Toaster />
    </BrowserRouter>
  </ErrorBoundary>
);
};

export default App;

